import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { Target, DollarSign, Users, TrendingUp, Percent, Award, FileText, Home, Hammer, PenTool, MessageSquare, Calendar, UserCog } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { getUserGoals } from '../services/firebase/userGoalsService';
import { getCompanyIdForUser, findAccountIdByEmail, getPrimaryUserIdForAccount } from '../services/firestoreService';
import PortalBackground from '../components/PortalBackground';

// Sample data
const financialData = {
  revenue: {
    current: 750000,
    goal: 1000000,
    startPoint: 500000,
    data: [
      { month: 'Jan', value: 520000 },
      { month: 'Feb', value: 540000 },
      { month: 'Mar', value: 560000 },
      { month: 'Apr', value: 580000 },
      { month: 'May', value: 600000 },
      { month: 'Jun', value: 650000 },
      { month: 'Jul', value: 700000 },
      { month: 'Aug', value: 750000 },
      { month: 'Sep', value: 800000 },
      { month: 'Oct', value: 850000 },
      { month: 'Nov', value: 900000 },
      { month: 'Dec', value: 1000000 }
    ]
  }
};

// Progress bar component
const ProgressBar = ({ value, startPoint, goal, label, color = "bg-blue-500", prefix = "$" }) => {
  const range = goal - startPoint;
  const currentPercentage = Math.min(100, Math.max(0, ((value - startPoint) / range) * 100));
  
  return (
    <div className="mb-6">
      <div className="flex justify-between mb-1">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className="text-sm font-medium text-gray-700">{Math.round(currentPercentage)}% Complete</span>
      </div>
      <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full ${color} rounded-full`} 
          style={{ width: `${currentPercentage}%` }}
        ></div>
      </div>
      <div className="flex justify-between mt-1 text-xs text-gray-500">
        <span>{prefix}{startPoint.toLocaleString()}</span>
        <span className="font-semibold">{prefix}{value.toLocaleString()}</span>
        <span>{prefix}{goal.toLocaleString()}</span>
      </div>
    </div>
  );
};

// Metric Box component
const MetricBox = ({ icon, title, value, subtitle, color = "text-blue-600" }) => (
  <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
    <div className="flex items-center mb-4">
      <div className={`${color} mr-2`}>
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
    </div>
    <div className="text-2xl font-bold text-gray-900 mb-1">{value}</div>
    {subtitle && <div className="text-sm text-gray-500">{subtitle}</div>}
  </div>
);

const RevenueInputForm = ({ onSubmit, isEditing, setIsEditing }) => {
  const currentYear = new Date().getFullYear();
  const [formData, setFormData] = useState({
    currentRevenue: '',
    threeYearsAgo: '',
    twoYearsAgo: '',
    lastYear: '',
    thisYearGoal: '',
    nextYearGoal: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Remove any non-numeric characters and convert to number
    const numericValue = value.replace(/[^0-9]/g, '');
    setFormData(prev => ({
      ...prev,
      [name]: numericValue
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = Object.keys(formData).reduce((acc, key) => {
      acc[key] = parseInt(formData[key], 10) || 0;
      return acc;
    }, {});
    onSubmit(data);
    setIsEditing(false);
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-800">Revenue Data</h2>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Revenue
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">$</span>
              <input
                type="text"
                name="currentRevenue"
                value={formData.currentRevenue}
                onChange={handleChange}
                className="pl-7 block w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
                placeholder="Enter current revenue"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Revenue {currentYear - 3}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">$</span>
              <input
                type="text"
                name="threeYearsAgo"
                value={formData.threeYearsAgo}
                onChange={handleChange}
                className="pl-7 block w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
                placeholder={`Enter ${currentYear - 3} revenue`}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Revenue {currentYear - 2}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">$</span>
              <input
                type="text"
                name="twoYearsAgo"
                value={formData.twoYearsAgo}
                onChange={handleChange}
                className="pl-7 block w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
                placeholder={`Enter ${currentYear - 2} revenue`}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Revenue {currentYear - 1}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">$</span>
              <input
                type="text"
                name="lastYear"
                value={formData.lastYear}
                onChange={handleChange}
                className="pl-7 block w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
                placeholder={`Enter ${currentYear - 1} revenue`}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {currentYear} Goal
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">$</span>
              <input
                type="text"
                name="thisYearGoal"
                value={formData.thisYearGoal}
                onChange={handleChange}
                className="pl-7 block w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
                placeholder={`Enter ${currentYear} goal`}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {currentYear + 1} Goal
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">$</span>
              <input
                type="text"
                name="nextYearGoal"
                value={formData.nextYearGoal}
                onChange={handleChange}
                className="pl-7 block w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
                placeholder={`Enter ${currentYear + 1} goal`}
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            type="submit"
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Save Revenue Data
          </button>
        </div>
      </form>
    </div>
  );
};

const Sales = () => {
  const { currentUser } = useAuth();
  const [isEditing, setIsEditing] = useState(true);
  const [revenueData, setRevenueData] = useState(null);
  const [userGoals, setUserGoals] = useState(null);
  const [goalsLoading, setGoalsLoading] = useState(true);
  const [goalsOwnerId, setGoalsOwnerId] = useState(null);

  const handleRevenueSubmit = (data) => {
    const currentYear = new Date().getFullYear();
    const chartData = [
      { year: currentYear - 3, value: data.threeYearsAgo },
      { year: currentYear - 2, value: data.twoYearsAgo },
      { year: currentYear - 1, value: data.lastYear },
      { year: currentYear, value: data.currentRevenue },
      { year: currentYear, goal: data.thisYearGoal },
      { year: currentYear + 1, goal: data.nextYearGoal }
    ];
    setRevenueData({
      current: data.currentRevenue,
      goal: data.thisYearGoal,
      startPoint: data.lastYear,
      chartData
    });
  };

  // Resolve shared goals owner so all team members see the same sales goals
  useEffect(() => {
    if (!currentUser?.uid) {
      setGoalsOwnerId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        let ownerId = currentUser.uid;
        let cid = await getCompanyIdForUser(currentUser.uid);
        if (!cid && currentUser.email) {
          cid = await findAccountIdByEmail(currentUser.email, 'Clients').catch(() => null);
        }
        if (cid) {
          const primaryUid = await getPrimaryUserIdForAccount(cid);
          if (primaryUid) {
            ownerId = primaryUid;
          }
        }
        if (!cancelled) {
          setGoalsOwnerId(ownerId);
        }
      } catch (error) {
        console.error('Error resolving sales goals owner id:', error);
        if (!cancelled) {
          setGoalsOwnerId(currentUser.uid);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid, currentUser?.email]);

  const formatGoalValue = (value, unit) => {
    if (value === null || value === undefined) return '0';
    if (unit === 'usd') {
      return `$${Number(value).toLocaleString()}`;
    }
    if (unit === 'percent') {
      return `${value}%`;
    }
    return value.toString();
  };

  // Load shared sales goals
  useEffect(() => {
    const loadUserGoals = async () => {
      if (!currentUser || !goalsOwnerId) return;
      try {
        setGoalsLoading(true);
        const goals = await getUserGoals(goalsOwnerId);
        setUserGoals(goals);
      } catch (error) {
        console.error('Error loading sales goals:', error);
      } finally {
        setGoalsLoading(false);
      }
    };

    loadUserGoals();
  }, [currentUser, goalsOwnerId]);

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative">
      <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white mb-6 text-center">Sales Dashboard</h1>
      
      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <MetricBox
          icon={<Target className="w-6 h-6" />}
          title="Close Ratio"
          value="65%"
          subtitle="Last 30 days"
          color="text-emerald-600"
        />
        <MetricBox
          icon={<DollarSign className="w-6 h-6" />}
          title="Average Job Size"
          value="$85,000"
          subtitle="Last 12 months"
          color="text-blue-600"
        />
        <MetricBox
          icon={<Users className="w-6 h-6" />}
          title="Referrals"
          value="24"
          subtitle="This month"
          color="text-purple-600"
        />
        <MetricBox
          icon={<TrendingUp className="w-6 h-6" />}
          title="Project Runway"
          value="2.8 months"
          subtitle="Based on current burn rate"
          color="text-orange-600"
        />
        <MetricBox
          icon={<Users className="w-6 h-6" />}
          title="Referral Programs"
          value="12"
          subtitle="Active programs"
          color="text-indigo-600"
        />
        <MetricBox
          icon={<Home className="w-6 h-6" />}
          title="Realtor Partners"
          value="45"
          subtitle="Active partnerships"
          color="text-pink-600"
        />
        <MetricBox
          icon={<Hammer className="w-6 h-6" />}
          title="Pardon Our Dust"
          value="8"
          subtitle="Active projects"
          color="text-yellow-600"
        />
        <MetricBox
          icon={<PenTool className="w-6 h-6" />}
          title="Design Contracts"
          value="15"
          subtitle="This month"
          color="text-cyan-600"
        />
        <MetricBox
          icon={<MessageSquare className="w-6 h-6" />}
          title="Sales Scripts"
          value="6"
          subtitle="Active scripts"
          color="text-red-600"
        />
        <MetricBox
          icon={<Award className="w-6 h-6" />}
          title="Awards"
          value="3"
          subtitle="This year"
          color="text-amber-600"
        />
      </div>
      
      {isEditing ? (
        <RevenueInputForm 
          onSubmit={handleRevenueSubmit} 
          isEditing={isEditing}
          setIsEditing={setIsEditing}
        />
      ) : revenueData && (
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Target className="text-emerald-600 mr-2" size={24} />
              <h2 className="text-xl font-semibold text-gray-800">Revenue</h2>
            </div>
            <button
              onClick={() => setIsEditing(true)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Edit Data
            </button>
          </div>
          
          <ProgressBar 
            value={revenueData.current} 
            startPoint={revenueData.startPoint} 
            goal={revenueData.goal} 
            label="Annual Revenue" 
            color="bg-emerald-500"
          />
          
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-700 mb-2">Revenue Trend</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={revenueData.chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => ['$' + value.toLocaleString(), 'Revenue']}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    name="Actual Revenue" 
                    stroke="#10B981" 
                    strokeWidth={2}
                    dot={{ r: 6 }}
                    activeDot={{ r: 8 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="goal" 
                    name="Goal" 
                    stroke="#60A5FA" 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Sales Goals Card (shared company-wide) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-center mb-4">
              <Target className="text-2xl text-red-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-800">Sales Goals</h2>
            </div>
            <div className="space-y-4">
              {goalsLoading ? (
                <div className="flex items-center justify-center min-h-[100px]">
                  <BBALoader size={32} />
                </div>
              ) : userGoals && userGoals.Sales && userGoals.Sales.length > 0 ? (
                userGoals.Sales.slice(0, 3).map((goal, index) => {
                  const progress = goal.target > 0 ? (goal.actual / goal.target) * 100 : 0;
                  let currentColor = 'bg-gray-100 text-gray-800';
                  if (progress >= 100) {
                    currentColor = 'bg-green-100 text-green-800';
                  } else if (progress >= 70) {
                    currentColor = 'bg-yellow-100 text-yellow-800';
                  } else if (progress > 0) {
                    currentColor = 'bg-red-100 text-red-800';
                  }
                  return (
                    <div key={goal.id || index} className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl">
                      <div className="text-center mb-2">
                        <h3 className="font-medium text-gray-800 text-base">{goal.metricName}</h3>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-green-100 p-2 rounded-lg text-center">
                          <div className="text-xs text-green-700 font-medium mb-1">Target</div>
                          <div className="text-sm font-bold text-green-800">
                            {formatGoalValue(goal.target, goal.unit)}
                          </div>
                        </div>
                        <div className={`${currentColor} p-2 rounded-lg text-center`}>
                          <div className="text-xs font-medium mb-1">Current</div>
                          <div className="text-sm font-bold">
                            {formatGoalValue(goal.actual, goal.unit)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="flex flex-col items-center justify-center min-h-[100px]">
                  <Target className="w-8 h-8 text-gray-400 mb-2" />
                  <p className="text-gray-500 text-sm text-center mb-3">No sales goals set yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Schedule Call and Ask a Question Row */}
      <div className="grid grid-cols-2 gap-6">
        <div 
          className="p-6 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
          onClick={() => {
            window.open('https://calendly.com/ken-zwz1/meet-with-ken', '_blank');
          }}
        >
          <div className="flex items-center justify-center mb-4">
            <Calendar className="text-4xl text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-center mb-4 text-gray-800">Schedule a Call</h2>
          <div className="p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl text-center">
            <p className="text-sm text-gray-600">Book a consultation</p>
          </div>
        </div>

        <div 
          className="p-6 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
          onClick={() => {
            alert('Question submission feature coming soon!');
          }}
        >
          <div className="flex items-center justify-center mb-4">
            <UserCog className="text-4xl text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-center mb-4 text-gray-800">Ask a Question</h2>
          <div className="p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl text-center">
            <p className="text-sm text-gray-600">Get help from our team</p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Sales; 